import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useDriverShiftBooking } from "@/hooks/useDriverShiftBooking";
import { DriverShiftBooking } from "./DriverShiftBooking";
import { Calendar, AlertTriangle, X, Clock } from "lucide-react";

/**
 * This component shows a prompt/modal when a driver has < 5 shifts remaining.
 * - Popup appears on login if < 5 shifts
 * - Persistent urgent banner if <= 3 shifts
 * - Critical pulsing banner if <= 1 shift
 */
export const ShiftBookingPrompt = () => {
  const [dismissed, setDismissed] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  
  const {
    driverId,
    driverName,
    remainingShiftsCount,
    shouldPromptForBooking,
    isUrgent,
    isCritical,
    isLoading,
  } = useDriverShiftBooking();
  
  // Reset dismissed state when shifts count changes significantly
  useEffect(() => {
    if (remainingShiftsCount >= 5) {
      setDismissed(false);
      setShowBooking(false);
    }
  }, [remainingShiftsCount]);
  
  // Don't show anything if not a driver, still loading, or already dismissed (and not urgent)
  if (!driverId || isLoading) {
    return null;
  }

  // If urgent, show dismissible banner (not blocking navigation)
  if (isUrgent && !showBooking && !dismissed) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fade-in">
        <div className={`rounded-lg shadow-xl p-4 border ${isCritical ? 'bg-destructive text-destructive-foreground border-destructive/50' : 'bg-warning text-warning-foreground border-warning/50'}`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">
                  {isCritical 
                    ? `🚨 ${remainingShiftsCount === 0 ? 'NO' : 'Only ' + remainingShiftsCount} shift${remainingShiftsCount !== 1 ? 's' : ''} remaining!`
                    : `⚠️ Only ${remainingShiftsCount} shift${remainingShiftsCount !== 1 ? 's' : ''} remaining`
                  }
                </span>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 hover:bg-white/20 -mt-1 -mr-1"
                  onClick={() => setDismissed(true)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs opacity-90 mt-1">Book your shifts to avoid missing work.</p>
              <Button 
                size="sm" 
                variant="secondary"
                className="mt-2 h-7 text-xs"
                onClick={() => setShowBooking(true)}
              >
                <Calendar className="h-3 w-3 mr-1" />
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Regular prompt (< 5 shifts but not urgent)
  if (!shouldPromptForBooking || dismissed) {
    return null;
  }
  
  // Show full booking modal
  if (showBooking) {
    return (
      <Dialog open onOpenChange={(open) => !open && setShowBooking(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DriverShiftBooking forceShow onClose={() => setShowBooking(false)} />
        </DialogContent>
      </Dialog>
    );
  }
  
  // Show initial prompt banner (non-urgent)
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fade-in">
      <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-lg shadow-xl p-4 border border-primary/50">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Clock className="h-5 w-5" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-sm">
                {remainingShiftsCount === 0 
                  ? "No Upcoming Shifts!" 
                  : `${remainingShiftsCount} Shift${remainingShiftsCount !== 1 ? 's' : ''} Scheduled`
                }
              </h4>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 hover:bg-white/20"
                onClick={() => setDismissed(true)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <p className="text-xs opacity-90 mt-1">
              Book your shifts for the next 7 days to secure your preferred times.
            </p>
            
            <div className="flex gap-2 mt-3">
              <Button 
                size="sm" 
                variant="secondary" 
                className="bg-white/20 hover:bg-white/30 text-white border-0 text-xs h-8"
                onClick={() => setShowBooking(true)}
              >
                <Calendar className="h-3 w-3 mr-1" />
                Book Shifts Now
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                className="hover:bg-white/10 text-xs h-8"
                onClick={() => setDismissed(true)}
              >
                Later
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
